import '../css/financial-calendar.scss';
import EasyMarketsData from 'Enums/easymarkets/easymarkets.data';
import EasyMarketsService from 'Services/easymarkets.service';
import { toDealSizeLocale } from 'Enums/dealsize.enum';

const dealSizeDomain = 'dealsize.ai';
const DEALSIZE_ORIGIN = EasyMarketsService.dealsizeOrigin();
const DEALSIZE_SCRIPT_URL = `${DEALSIZE_ORIGIN}/embed-widget.js`;
const DEALSIZE_MESSAGE_HANDLER_KEY = '__easyMarketsDealSizeMessageHandler';
const DEALSIZE_ANALYSIS_HIDDEN_CULTURES = ['eu', 'au'];
const DEALSIZE_STRICT_SOURCES = ['add-to-calendar', 'continue-to-app'];
const DEALSIZE_OPEN_SOURCES = ['analysis-link'];
const DEALSIZE_CONTAINERS = [
	{
		id: 'financial-calendar-dealsize-widget',
	},
	{
		id: 'widget-dark-es',
	},
];

function getDealSizeLocale(container) {
	return toDealSizeLocale(container.dataset.dealsizeLocale || document.documentElement.lang);
}

function shouldShowAnalysisButton() {
	const culture = window.easymarkets?.data?.culture || '';

	return !DEALSIZE_ANALYSIS_HIDDEN_CULTURES.includes(culture.toLowerCase());
}

function openDealSizeUrl(url, source) {
	if (DEALSIZE_STRICT_SOURCES.includes(source)) {
		// DealSize already handles calendar navigation from the originating tap.
		// Do not open the postMessage URL again in normal page context.
		return;
	}

	window.open(url, '_blank', 'noopener,noreferrer');
}

function initDealSizeContainer(config) {
	const container = document.getElementById(config.id);

	if (!container || container.dataset.dealsizeLoaded === 'true') {
		return;
	}

	container.dataset.dealsizeLoaded = 'true';

	const script = document.createElement('script');
	script.src = DEALSIZE_SCRIPT_URL;
	script.async = true;
	script.setAttribute('data-container-id', config.id);
	script.setAttribute('data-primary-color', '#075C8F');
	script.setAttribute('data-secondary-color', '#c0e1ae');
	script.setAttribute('data-locale', getDealSizeLocale(container));
	script.setAttribute('data-show-analysis-button', String(shouldShowAnalysisButton()));
	script.setAttribute('data-show-add-to-calendar-button', String(!EasyMarketsData.isWidget()));

	document.body.appendChild(script);
}

function addDealSizeMessageHandler() {
	if (window[DEALSIZE_MESSAGE_HANDLER_KEY]) {
		return;
	}

	window[DEALSIZE_MESSAGE_HANDLER_KEY] = true;

	window.addEventListener('message', (event) => {
		// check the origin of the message to ensure it's from the expected source
		if (event.origin !== DEALSIZE_ORIGIN) return;

		// verify the structure of the message to ensure it contains the expected data
		const data = event.data;
		if (data?.type !== 'dealsize-widget' || data?.widget !== 'calendar' || data?.action !== 'openUrl') return;

		// validate the URL to ensure it's a valid URL before proceeding
		let url;
		try {
			url = new URL(data.url);
		} catch (error) {
			return;
		}

		if (DEALSIZE_STRICT_SOURCES.includes(data.source)) {
			const allowedHosts = ['calendar.google.com', dealSizeDomain]; // strip www before comparing
			if (!allowedHosts.includes(url.hostname.replace(/^www\./, ''))) return;
		} else if (DEALSIZE_OPEN_SOURCES.includes(data.source)) {
			if (url.protocol !== 'https:') return;
		} else {
			return;
		}

		openDealSizeUrl(url.toString(), data.source);
	});
}

function initDealSizeWidgets() {
	const hasDealSizeContainer = DEALSIZE_CONTAINERS.some(config => document.getElementById(config.id));

	if (!hasDealSizeContainer) {
		return;
	}

	DEALSIZE_CONTAINERS.forEach(initDealSizeContainer);
	addDealSizeMessageHandler();
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initDealSizeWidgets);
} else {
	initDealSizeWidgets();
}
